import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { styled } from "@mui/system";
import {
    FirstPageRounded,
    LastPageRounded,
    ChevronLeftRounded,
    ChevronRightRounded,
    Visibility,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import styles from "./Diners.module.css";
import { useLoaderContext, useToastContext } from '../../../Context/ToastContext';
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import { useUserContext } from "../../../Context/UserContext";
import ViewDocument from "../ViewDocument/ViewDocument";
import { Helmet } from "react-helmet";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import animatedAvatarUrl from "../../../images/man.png";
import { apiGet, apiPost } from "../../../utils/ApiManager";
import { useRef } from "react";

const fetchAdminDiners = process.env.REACT_APP_FETCH_DINERS
const getToggleStatus = process.env.REACT_APP_HANDLE_TOGGLE_STATUS

const Diners = () => {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [selectedDiner, setSelectedDiner] = useState(null);
    const { showToast } = useToastContext();
    const { setLoading } = useLoaderContext();
    const { getToken, requestConfirmation } = useUserContext();
    const [showDocument, setShowDocument] = useState(false);
    const [documentUrl, setDocumentUrl] = useState("");
    const navigate = useNavigate();

    const handleChangePage = (event, newPage) => {
        setPage(newPage + 1);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value));
        setPage(1);
    };

    const handleCloseDocument = () => {
        setDocumentUrl("");
        setShowDocument(false);
    };



    const fetchDiners = async () => {
        try {
            setLoading(true);
            let query = {
                pageSize: rowsPerPage,
                pageNo: page,
                searchText: searchQuery
            }
            query = new URLSearchParams(query).toString();
            const response = await apiGet(`${fetchAdminDiners}?${query}`);
            if (response.success) {
                setData(response?.data || []);
                setTotalCount(response?.totalCount || 0);
            } else {
                showToast(response?.message || "Error fetching restaurant list.", "error");
            }
        } catch (error) {
            showToast(error?.response?.message || error?.message || "Error fetching restaurant list.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (userData, isCurrentlyActive) => {
        try {
            const result = await requestConfirmation(`Want to ${!isCurrentlyActive ? "Activate" : "Deactivate"} ${userData?.firstName} ${userData?.lastName} ?`);
            if (!result) return;
            setLoading(true);
            const response = await apiPost(`${getToggleStatus}/${isCurrentlyActive ? "user-block" : "user-unblock"}/${userData?.userId}`, {});

            if (response.success) {
                showToast(`Diner ${isCurrentlyActive ? "deactivated" : "activated"} successfully.`, "success");
                fetchDiners();
            } else {
                showToast(response?.message || "Action failed.", "error");
            }
        } catch (error) {
            showToast(error?.response?.message || error?.message || "Error updating diner status.", "error");
        } finally {
            setLoading(false);
        }
    };


    const fetchDinerDetails = async (userId) => {
        try {
            setLoading(true)
            const response = await apiGet(`${fetchAdminDiners}/${userId}`);
            if (response?.success) {
                setSelectedDiner(response.data || null);
                setOpenModal(true);
            } else {
                showToast(response?.message || "Failed to fetch diner details !", "error")
            }
        } catch (error) {
            showToast(error?.response?.message || error?.message || "Failed to fetch diner details !", "error")
            setSelectedDiner(null);
            setOpenModal(true);
        } finally {
            setLoading(false)
        }
    };


    const handleViewBooking = () => {
        navigate(`/bookings/user/${selectedDiner?.userId}`);
    }

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedDiner(null);
    };

    const isFirstLoadRef = useRef(true);

    useEffect(() => {
        fetchDiners();
    }, [page, rowsPerPage]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (searchQuery.length > 1) {
                fetchDiners();
            }else{
                if(isFirstLoadRef.current) {
                    isFirstLoadRef.current = false
                    return;
                }
                setData([]);
                fetchDiners();
            }
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchQuery]);

    return (
        <Box className={styles.mainContainer}>
            <Helmet>
                <title>Diners | DiNEST</title>
                <meta name="description" content="View diners on DiNEST" />
            </Helmet>
            <Box className={styles.restaurantListContainer}>
                <Typography variant="h4" className={styles.containerTitle}>
                    Diners
                </Typography>

                {(data?.length > 0 || searchQuery) ? (
                    <>
                        <div className={styles.searchBarContainer}>
                            <TextField
                                label="Search Diners"
                                variant="outlined"
                                fullWidth
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={styles.searchBar}
                                sx={{ my: 2 }}
                            />
                        </div>

                        <TableContainer className={styles.tableContainer} component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={styles.tableHead}>Sr. No</TableCell>
                                        <TableCell className={styles.tableHead}>User Id</TableCell>
                                        <TableCell className={styles.tableHead}>Name</TableCell>
                                        <TableCell className={styles.tableHead}>Email</TableCell>
                                        <TableCell className={styles.tableHead}>Phone</TableCell>
                                        <TableCell className={styles.tableHead}>Last Login</TableCell>
                                        <TableCell className={styles.tableHead}>Status</TableCell>
                                        <TableCell className={styles.tableHead}>View</TableCell>
                                        <TableCell className={styles.tableHead}>Activate/Deactivate</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((diner, index) => (
                                        <TableRow key={diner.userId} style={{ opacity: diner?.isDeleted ? 0.4 : 1 }} className={styles.tableDataContainer}>
                                            <TableCell className={styles.tableData}>
                                                {rowsPerPage * (page - 1) + index + 1}
                                            </TableCell>
                                            <TableCell className={styles.tableData}>
                                                {diner?.userId || 'N/A'}
                                            </TableCell>
                                            <TableCell className={styles.tableData}>
                                                {`${diner?.firstName || 'N/A'} ${diner?.lastName || ''}`}
                                            </TableCell>
                                            <TableCell className={styles.tableData}>
                                                {diner?.email || 'N/A'}
                                            </TableCell>
                                            <TableCell className={styles.tableData}>
                                                {diner?.phoneNo || 'N/A'}
                                            </TableCell>
                                            <TableCell className={styles.tableData}>
                                                {diner?.lastLogin
                                                    ? new Date(diner.lastLogin).toLocaleString('en-GB', { hour12: true })
                                                    : "N/A"}
                                            </TableCell>
                                            <TableCell className={styles.tableData}>
                                                <StatusLabel active={diner?.isActive}>
                                                    {diner?.isActive ? "Active" : "Inactive"}
                                                </StatusLabel>
                                            </TableCell>
                                            <TableCell className={styles.tableData}>
                                                <Tooltip arrow title="View Diner">
                                                    <IconButton onClick={() => fetchDinerDetails(diner.userId)}>
                                                        <Visibility sx={{ color: '#207d46' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell className={styles.tableData}>
                                                <Button
                                                    variant="contained"
                                                    color={diner?.isActive ? "error" : "success"}
                                                    size="small"
                                                    onClick={() => handleToggleStatus(diner, diner?.isActive)}
                                                >
                                                    {diner?.isActive ? "Deactivate" : "Activate"}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <div className={styles.paginationContainer}>
                            <TablePagination
                                className={styles.pagination}
                                count={totalCount}
                                page={page - 1}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 15]}
                                sx={{
                                    '& .MuiTablePagination-actions button': {
                                        backgroundColor: 'transparent',
                                        color: 'white',
                                    },
                                    '& .MuiSelect-icon': {
                                        fill: 'white',
                                    }
                                }}
                                slotProps={{
                                    actions: {
                                        showFirstButton: true,
                                        showLastButton: true,
                                        slots: {
                                            firstPageIcon: FirstPageRounded,
                                            lastPageIcon: LastPageRounded,
                                            nextPageIcon: ChevronRightRounded,
                                            backPageIcon: ChevronLeftRounded,
                                        },
                                    },
                                }}
                            />
                        </div>
                    </>
                ) : (
                    <h2 style={{ color: "red", textAlign: 'center', marginTop: '2rem' }}>No diners registered yet !</h2>
                )}

                <Dialog open={openModal} fullWidth
                    maxWidth="xs" onClose={handleCloseModal}>
                    <Box className={styles.closeIconContainer}>
                        <Typography variant="h5" className={styles.title}>
                            Diner's Details
                        </Typography>
                        <div onClick={() => setOpenModal(false)} className={styles.backIconContainer}>
                            <ArrowBackIcon className={styles.backIcon} />
                        </div>
                    </Box>

                    <DialogContent className={styles.formScrollable}>
                        {selectedDiner ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {/* Profile Image */}
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <img
                                        src={selectedDiner?.profileImage || animatedAvatarUrl}
                                        alt="Profile"
                                        style={{ width: 100, height: 100, borderRadius: "50%" }}
                                    />
                                </Box>

                                {/* Basic Info */}
                                <Typography><strong>Diner ID:</strong> {selectedDiner.userId || 'N/A'}</Typography>
                                <Typography><strong>Name:</strong> {`${selectedDiner.firstName || 'N/A'} ${selectedDiner.lastName || ''}`}</Typography>
                                <Typography><strong>Email:</strong> {selectedDiner.email || 'N/A'}</Typography>
                                <Typography><strong>Phone:</strong> {selectedDiner.phoneNo || 'N/A'}</Typography>
                                <Typography><strong>Role:</strong> {selectedDiner.role || 'N/A'}</Typography>
                                <Typography><strong>Bio:</strong> {selectedDiner.bio || 'N/A'}</Typography>
                                <Typography><strong>Last login:</strong> {selectedDiner.lastLogin ? new Date(selectedDiner.lastLogin).toLocaleString('en-GB', { hour12: true }) : "N/A"}</Typography>
                                <Typography><strong>Account created at:</strong> {selectedDiner.createdAt ? new Date(selectedDiner.createdAt).toLocaleString('en-GB', { hour12: true }) : 'N/A'}</Typography>
                                <Typography><strong>Account status:</strong> {selectedDiner.isActive ? "Active" : "Inactive"}</Typography>
                            </Box>
                        ) : (
                            <Typography>No data available.</Typography>
                        )}
                        <Button sx={{ mt: 1.5 }} onClick={handleViewBooking} variant="outlined" >View all bookings</Button>
                    </DialogContent>
                </Dialog>
            </Box>
            <ViewDocument documentUrl={documentUrl} isOpen={showDocument} onClose={handleCloseDocument} />
        </Box >
    );
};

const StatusLabel = styled("span")(({ active }) => ({
    color: active ? "#4CAF50" : "#f44336",
    fontWeight: "bold",
    textTransform: "uppercase",
}));

export default Diners;
